import { useEffect, useState } from "react";
import { SiteFooter } from "@/components/SiteFooter";
import { Logo } from "@/components/Logo";
import {
  ArrowRight,
  CalendarDays,
  Gauge,
  Lock,
  ShieldCheck,
  Split,
  Users,
} from "lucide-react";
import { ApplyForm } from "@/components/ApplyForm";
import { ApplyModal } from "@/components/ApplyModal";
import { api } from "@/lib/api";
import { SeatBadge, SeatBar, SeatProof, SeatSticky, hasSeats } from "@/components/SeatMeter";
import { AdminMock, BlockedMock, CalendarMock, DeskMock, HistoryMock } from "@/components/AppMocks";

const Section = ({ children, className = "" }) => (
  <section className={`px-5 sm:px-8 lg:px-12 max-w-[1240px] mx-auto ${className}`}>
    {children}
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="text-[10px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-emerald-500">
    {children}
  </p>
);

const FEATURES = [
  {
    icon: Lock,
    kicker: "Your worst day, capped",
    title: "You can't have the day that wipes out the month",
    body: "Once you've reached your limit for the day, the trade simply won't go through — so a bad morning stays a bad morning.",
    visual: BlockedMock,
  },
  {
    icon: Split,
    kicker: "Nothing left to remember",
    title: "Your open trades get managed while you sit still",
    body: "You don't have to babysit a trade or talk yourself into holding it — your plan gets carried out for you, every time.",
    visual: HistoryMock,
  },
  {
    icon: CalendarDays,
    kicker: "Honest scoreboard",
    title: "You finally know what your trading actually looks like",
    body: "Every day is there in front of you, exactly as it happened, so review takes ten minutes instead of an evening.",
    visual: CalendarMock,
  },
  {
    icon: Users,
    kicker: "For desks and mentors",
    title: "You know how every trader is doing, without asking",
    body: "One screen shows you who's up, who's off for the day and who needs a conversation — while everyone trades their own account.",
    visual: AdminMock,
  },
];

const STEPS = [
  {
    n: "01",
    t: "We talk first",
    d: "A 1-1 call to look at your account and what actually breaks your month. If the desk isn't right for you, we'll say so.",
  },
  {
    n: "02",
    t: "Your limits get agreed",
    d: "We agree what a good day and a bad day look like for you. After that, changing them takes a conversation, not a click.",
  },
  {
    n: "03",
    t: "You just trade",
    d: "Connect your own MT5 account and place your trades. The ones that break what you agreed to don't go through.",
  },
];

export default function Landing() {
  const [seats, setSeats] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const openApply = () => setApplyOpen(true);

  useEffect(() => {
    api
      .get("/public/config")
      .then(({ data }) => setSeats(data))
      .catch(() => {});
  }, []);

  return (
    <div
      className={`min-h-screen bg-[#08090A] text-zinc-100 antialiased selection:bg-emerald-500/30 ${
        hasSeats(seats) ? "pb-20 md:pb-0" : ""
      }`}
    >
      {/* nav */}
      <header className="sticky top-0 z-50 border-b border-zinc-900/80 bg-black/70 backdrop-blur-xl">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-12 h-14 flex items-center gap-3">
          <Logo size={26} />
          <span className="hidden sm:block font-mono text-[10px] text-zinc-600 border border-zinc-800 rounded-sm px-2 py-1">
            MT5 · INVITE ONLY
          </span>
          <div className="flex-1" />
          <a
            href="/login"
            data-testid="landing-login-link"
            className="text-xs uppercase tracking-[0.12em] font-semibold text-zinc-400 hover:text-zinc-100 transition-colors duration-200"
          >
            Trader login
          </a>
          <button
            type="button"
            onClick={openApply}
            data-testid="landing-nav-apply"
            className="rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black px-3.5 py-2 text-[11px] uppercase tracking-[0.12em] font-bold transition-colors duration-200 active:scale-95"
          >
            Apply
          </button>
        </div>
      </header>

      <SeatSticky seats={seats} onApply={openApply} />

      {/* hero */}
      <Section className={`pb-14 ${hasSeats(seats) ? "pt-14 md:pt-28" : "pt-14 sm:pt-24"}`}>
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Eyebrow>Your trading buddy</Eyebrow>
              <SeatBadge seats={seats} />
            </div>
            <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight leading-[1.08]">
              Most people don't lose because of bad analysis. They lose because they{" "}
              <span className="text-emerald-400">break their own rules.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl">
              Strict RR enforces your rules on every trade — systematically, on your own trading
              account.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={openApply}
                data-testid="hero-apply-btn"
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black px-6 py-4 text-xs uppercase tracking-[0.14em] font-bold transition-colors duration-200 active:scale-95"
              >
                Book a 1-1 call <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="#features"
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-zinc-800 hover:border-zinc-600 px-6 py-4 text-xs uppercase tracking-[0.14em] font-semibold text-zinc-300 transition-colors duration-200"
              >
                See the desk
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 font-mono text-[11px] text-zinc-600">
              <span className="text-emerald-600">▪</span> Your own MT5 account
              <span className="text-emerald-600">▪</span> Your own money, your own execution provider
              <span className="text-emerald-600">▪</span> Nothing to install
            </div>
            <p
              className="mt-5 text-[11px] leading-relaxed text-zinc-600 max-w-lg"
              data-testid="hero-disclaimer"
            >
              Risk-management software — not a broker, not a signal service, not financial advice. We
              make no promise of profit or returns. Active market participation carries risk of loss.
            </p>
          </div>
          <DeskMock />
        </div>
      </Section>

      {/* problem strip */}
      <div className="border-y border-zinc-900 bg-zinc-950/60">
        <Section className="py-8 grid sm:grid-cols-3 gap-6">
          {[
            ["“One more trade, I'll get it back.”", "That trade doesn't go through here."],
            ["“I'll just move my stop a little.”", "You can't. It stays where you put it."],
            ["“I'll write it down later.”", "You never do. It's already written down for you."],
          ].map(([h, d]) => (
            <div key={h}>
              <p className="font-heading font-semibold tracking-tight text-sm">{h}</p>
              <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{d}</p>
            </div>
          ))}
        </Section>
      </div>

      {/* features */}
      <div id="features" className="py-16 sm:py-24 space-y-20 sm:space-y-28">
        {FEATURES.map((f, i) => (
          <Section key={f.title}>
            <div
              className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                i % 2 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div>
                <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-sm px-2.5 py-1.5">
                  <f.icon className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                    {f.kicker}
                  </span>
                </div>
                <h2 className="mt-5 text-2xl sm:text-3xl lg:text-[2.5rem] font-heading font-bold tracking-tight leading-[1.1]">
                  {f.title}
                </h2>
                <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-xl">
                  {f.body}
                </p>
              </div>
              <f.visual />
            </div>
          </Section>
        ))}
      </div>

      {/* how it works */}
      <div className="border-y border-zinc-900 bg-zinc-950/60 py-16 sm:py-20">
        <Section>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-4 text-2xl sm:text-4xl font-heading font-bold tracking-tight">
            Three steps, then you just trade.
          </h2>
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="border border-zinc-800 bg-black/40 rounded-sm p-5 hover:border-emerald-800 transition-colors duration-300"
              >
                <span className="font-mono text-xs text-emerald-600">{s.n}</span>
                <h3 className="mt-3 font-heading font-semibold tracking-tight">{s.t}</h3>
                <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* apply */}
      <Section className="py-16 sm:py-24">
        <div id="apply" className="grid lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-24 order-2 lg:order-1">
            <Eyebrow>Invite only</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-bold tracking-tight leading-[1.08]">
              This is invite only.
              <br />
              Here's why.
            </h2>
            <p className="mt-5 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-lg">
              We keep the desk small so every trader gets attention and their own execution slot.
              Answer three quick screens, then book a 1-1 call — we'll go through your rules and set
              them up with you.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                [ShieldCheck, "You keep your execution provider and your funds — we never hold money"],
                [Gauge, "Your limits are agreed on the call, then held to"],
                [Users, "Small desk, direct access, no course, no signals"],
              ].map(([Icon, t]) => (
                <li key={t} className="flex items-start gap-3 text-sm text-zinc-300">
                  <Icon className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <SeatBar seats={seats} className="mt-7 max-w-md" />
          </div>
          <div className="space-y-4 order-1 lg:order-2">
            <SeatProof seats={seats} />
            <ApplyForm />
          </div>
        </div>
      </Section>

      <SiteFooter />
      <ApplyModal open={applyOpen} onOpenChange={setApplyOpen} />
    </div>
  );
}
